import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  GridApi,
  ValueGetterParams,
  ValueSetterParams,
  GridOptions,
} from 'ag-grid-community'
import {
  ColumnType,
  columnTypes,
  CURRENT_MONTH_BACKGROUND_COLOR_STYLE,
  defaultOnCellClicked,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import { DefaultCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import { intl } from '../../../../i18n'
import {
  ClientSideNumberFilter,
  ClientSideSelectFilter,
  ClientSideTextFilter,
} from '../../../containers/BulkSheetView/components/filter'
import DateVO from '../../../../vo/DateVO'
import { DateTerm } from '../../../../utils/date'
import { generateYearMonthList } from '../../ProfitLossItems/gridOptions'
import { UnitPriceBody } from '../../UnitPricePerPosition/UnitPricePerPosition'
import store from '../../../../store'
import { requireSave } from '../../../../store/requiredSaveData'
import _ from 'lodash'
import {
  isHalfOrFullWidthNumber,
  parseFullNumberToHalf,
} from '../../../../utils/number'
import { NumberCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer/NumberCellRenderer'

export const unitPricePerUsersGridOptions = (): GridOptions => {
  return {
    context: {},
    suppressLastEmptyLineOnPaste: true,
    // Row
    treeData: false,
    rowDragManaged: false,
    rowDragMultiRow: false,
    suppressMoveWhenRowDragging: true,
    enterNavigatesVerticallyAfterEdit: true,
    // Column
    columnTypes: columnTypes(),
    components: frameworkComponents,
    defaultColGroupDef: {
      suppressHeaderContextMenu: true,
    },
    defaultColDef: {
      cellDataType: false,
      width: 80,
      editable: false,
      enableValue: false,
      sortable: false,
      resizable: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      suppressSizeToFit: false,
      singleClickEdit: false,
      cellEditor: 'textEditor',
      cellRenderer: DefaultCellRenderer,
      onCellClicked: defaultOnCellClicked,
      cellClassRules: {
        'grid-edited-cell': params => {
          const { colDef, data, value } = params
          const field = colDef.field ?? colDef.colId
          if (
            !data.editedData ||
            !field ||
            !colDef.editable ||
            (typeof colDef.editable === 'function' && !colDef.editable(params))
          ) {
            return false
          }
          return (
            data.editedData.hasOwnProperty(field) &&
            value !== data.editedData[field]
          )
        },
      },
    },
    columnDefs: [
      {
        field: 'rowNumber',
        type: [ColumnType.sequenceNo],
        valueGetter: params => {
          return (params.node?.rowIndex ?? 0) + 1
        },
      },
      {
        groupId: 'information',
        headerName: intl.formatMessage({ id: 'unit.price.information' }),
        children: [
          {
            field: 'body.userCode',
            headerName: intl.formatMessage({ id: 'unit.price.userCode' }),
            width: 90,
            hide: false,
            pinned: true,
            lockPosition: 'left',
            sortable: true,
            valueGetter: (params: ValueGetterParams) => {
              return params.data.body.user?.code ?? ''
            },
            filter: ClientSideTextFilter,
            floatingFilter: true,
          },
          {
            field: 'body.user',
            headerName: intl.formatMessage({ id: 'unit.price.user' }),
            width: 120,
            hide: false,
            pinned: true,
            lockPosition: 'left',
            sortable: true,
            valueGetter: (params: ValueGetterParams) => {
              return params.data.body.user?.name ?? ''
            },
            filter: ClientSideSelectFilter,
            floatingFilter: true,
            filterParams: {
              getValue: option => option,
              getLabel: option => option,
            },
          },
          {
            field: 'body.division',
            headerName: intl.formatMessage({ id: 'unit.price.division' }),
            width: 100,
            hide: false,
            pinned: true,
            lockPosition: 'left',
            sortable: true,
            valueGetter: (params: ValueGetterParams) => {
              return params.data.body.user?.division?.displayName ?? ''
            },
            filter: ClientSideSelectFilter,
            floatingFilter: true,
            filterParams: {
              getValue: option => option,
              getLabel: option => option,
            },
          },
          {
            field: 'body.position',
            headerName: intl.formatMessage({ id: 'unit.price.position' }),
            width: 100,
            hide: false,
            pinned: true,
            lockPosition: 'left',
            sortable: true,
            valueGetter: (params: ValueGetterParams) => {
              return params.data.body.user?.position?.displayName ?? ''
            },
            filter: ClientSideSelectFilter,
            floatingFilter: true,
            filterParams: {
              getValue: option => option,
              getLabel: option => option,
            },
          },
          {
            field: 'unitPrice',
            headerName: intl.formatMessage({ id: 'unit.price' }),
            hide: false,
            pinned: true,
            lockPosition: 'left',
            sortable: true,
            cellClass: 'amount',
            cellRenderer: NumberCellRenderer,
            cellRendererParams: {
              numberWithCommas: true,
            },
            valueGetter: (params: ValueGetterParams) => {
              const units = params.data.body.unitPrices ?? []
              return getTargetUnitPrice(
                units,
                DateVO.now().getFirstDayOfMonth()
              )
            },
            filter: ClientSideNumberFilter,
            floatingFilter: true,
          },
        ],
      },
    ],
    excelStyles: [
      {
        id: 'amount',
        dataType: 'Number',
        numberFormat: {
          format: '#,###',
        },
      },
    ],
    processUnpinnedColumns: () => [],
  }
}

type RefreshDynamicColumnDefProps = {
  api: GridApi | null | undefined
  dateTerm: DateTerm
}

const UNIT_PRICE_FIELD_PREFIX = 'body.unitPrices.'

export const isUnitPriceColumn = (id: string | undefined) => {
  return !!id && id.startsWith(UNIT_PRICE_FIELD_PREFIX)
}

export const refreshDynamicColumnDef = ({
  api,
  dateTerm,
}: RefreshDynamicColumnDefProps) => {
  if (!api) return
  const yearMonths = generateYearMonthList(dateTerm)
  const yearList = Array.from(
    new Set(
      yearMonths.map(value => {
        return new DateVO(value).getYear()
      })
    )
  )
  const columnDefs = api.getColumnDefs()
  if (!columnDefs) return
  const newColDefs = columnDefs?.filter(def => {
    const field = (def as ColDef)?.field
    const groupId = (def as ColGroupDef)?.groupId
    return (
      (!!field && !field.startsWith(UNIT_PRICE_FIELD_PREFIX)) ||
      (!!groupId && !groupId.startsWith(UNIT_PRICE_FIELD_PREFIX))
    )
  })
  if (!_.isEmpty(yearMonths)) {
    const now = DateVO.now()
    const yearCols: ColGroupDef[] = yearList.map(year => {
      return {
        groupId: `${UNIT_PRICE_FIELD_PREFIX}${year}`,
        headerName: `${year} ${intl.formatMessage({ id: 'year' })}`,
        children: [],
      }
    })
    const firstDayOfMonth = now.getFirstDayOfMonth()
    yearMonths.forEach(month => {
      const date = new DateVO(month)
      const value = date.format('YYYY/MM')
      const isCurrentMonth = date.isEqual(firstDayOfMonth)
      const formattedDate = date.formatForApi()!
      const col: ColDef = {
        field: `${UNIT_PRICE_FIELD_PREFIX}${value}`,
        headerName: `${date.getMonth()} ${intl.formatMessage({ id: 'month' })}`,
        hide: false,
        pinned: false,
        suppressMovable: true,
        editable: true,
        cellClass: 'amount',
        cellRenderer: NumberCellRenderer,
        cellClassRules: {
          unit_price_input_month: params => {
            const units: UnitPriceBody[] = params.data.body?.unitPrices ?? []
            return units.some(v => v.validFrom === formattedDate)
          },
          unit_price_interval_month: params => {
            const units: UnitPriceBody[] = params.data.body?.unitPrices ?? []
            return !units.some(v => v.validFrom === formattedDate)
          },
        },
        cellStyle: (params: CellClassParams) => {
          return isCurrentMonth ? CURRENT_MONTH_BACKGROUND_COLOR_STYLE : {}
        },
        cellRendererParams: {
          numberWithCommas: true,
        },
        valueGetter: (params: ValueGetterParams) => {
          const units: UnitPriceBody[] = params.data.body?.unitPrices ?? []
          return getTargetUnitPrice(units, date)
        },
        valueSetter: (params: ValueSetterParams) => {
          const { colDef, data, oldValue, newValue } = params
          const field = colDef.field || colDef.colId
          const parseVal = parseFullNumberToHalf(newValue ?? '')
          if (
            (!!newValue && !isHalfOrFullWidthNumber(newValue)) ||
            parseVal.startsWith('.')
          ) {
            return false
          }
          const numVal = Number(parseVal ?? 0)
          if (
            !field ||
            (!oldValue && !newValue) ||
            oldValue === newValue ||
            Number.isNaN(numVal)
          ) {
            return false
          }
          const unitPrice = data.body.unitPrices.find(
            v => v.validFrom === date.formatForApi()
          )
          if (unitPrice && unitPrice.unitPrice === numVal) {
            return false
          }
          store.dispatch(requireSave())
          if (!unitPrice) {
            data.body.unitPrices.push({
              validFrom: date.formatForApi(),
              unitPrice: numVal,
            })
            data.edited = true
          } else if (!numVal) {
            data.body.unitPrices = data.body.unitPrices.filter(
              v => v.validFrom !== date.formatForApi()
            )
            data.edited = true
          } else {
            unitPrice.unitPrice = numVal
            data.edited = true
          }
          if (!data.editedData) {
            data.editedData = { [field]: oldValue }
          } else if (!data.editedData.hasOwnProperty(field)) {
            data.editedData[field] = oldValue
          }
          return true
        },
      }
      const yearHeader = yearCols.find(
        v => v.groupId === `${UNIT_PRICE_FIELD_PREFIX}${date.getYear()}`
      )
      yearHeader?.children.push(col)
    })
    yearCols.forEach(year => newColDefs?.push(year))
  }
  api.setGridOption('columnDefs', newColDefs)
}

const getTargetUnitPrice = (
  units: UnitPriceBody[],
  date: DateVO
): number | undefined => {
  const current = units.find(v => v.validFrom === date.formatForApi())
  if (current) {
    return current.unitPrice
  }
  const goneDays = units
    .map(v => new DateVO(v.validFrom))
    .filter(v => v.isBefore(date))
  if (goneDays && goneDays.length > 0) {
    const latest = DateVO.max(goneDays)
    return units.find(v => v.validFrom === latest.formatForApi())?.unitPrice
  }
  return undefined
}
